<template>
  <header>
    <div class="logo" @click="toggleSideBar">
      <font-awesome-icon icon="fa-solid fa-angles-right" v-if="!isSidebarOpen" />
    </div>
    <div class="actions">
      <ul class="actions-main-list">
        <!-- User Actions Section -->
        <li class="user_actions-list">
          <div class="user-img-container main-action">
            <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
          </div>
          <div class="nasted-actions-list-container">
            <ul class="nasted-actions-list">
              <li class="user-details-box">
                <div class="user-box">
                  <img loading="lazy" width="30" height="30" src="../assets/user.png">
                  <div>
                    <span class="user-email">{{ name }}</span>
                    <span class="user-role">{{ account_type }}</span>
                  </div>
                </div>
              </li>
              <li v-for="(action, index) in actions" :key="index" class="action-tap" @click="navigateTo(action.route)">
                <font-awesome-icon :icon="action.icon" />
                {{ action.title }}
              </li>
              <li class="action-tap Logout" @click="logOut">
                <font-awesome-icon icon="fa-solid fa-arrow-right-from-bracket" /> Logout
              </li>
            </ul>
          </div>
        </li>

        <!-- Notification Section -->
        <li>
          <div class="user-notification-container main-action" @mouseover.once="markAllNotificationsAsSeen">
            <el-badge v-if="notifications.unseen_count >0" :value="notifications.unseen_count" class="item" color="red">
              <font-awesome-icon icon="fa-solid fa-bell" />
            </el-badge>
            <font-awesome-icon icon="fa-solid fa-bell" v-if="notifications.unseen_count == 0"/>
            <div class="notification-container" @scroll="handleScroll">
              <ul class="notifications-list">
                <li v-for="(notification, index) in notifications.data" :key="index">
                  <div class="notification-header">
                    <span class="notification-title">{{ notification.title }}</span>
                    <span class="noti-date">{{ formatDate(notification.created_at) }}</span>
                  </div>
                  <p class="notification-message">{{ notification.description }}</p>
                </li>
              </ul>
              <div class="notification-container-footer" v-if="loading">
                <span>Loading...</span>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </header>
</template>

<script>
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { computed, onMounted, ref } from 'vue';

export default {
  name: 'NavBar',
  setup() {
    const store = useStore();
    const router = useRouter();
    const currentRoute = useRoute();
    
    // Local state
    const is_notifications_box_open = ref(false);
    const notifications = ref({ data: [], total: 0 });
    const page = ref(1);
    const hasMore = ref(true);
    const loading = ref(false);
    
    // Actions
    const actions = ref([
      {
        title: 'Profile',
        icon: 'fa-solid fa-user',
        route: '/profile',
      },
      {
        title: 'Billing',
        icon: 'fa-solid fa-bullhorn',
        route: null,
      },
      {
        title: 'Team',
        icon: 'fa-solid fa-people-group',
        route: null,
      },
      {
        title: 'Settings',
        icon: 'fa-solid fa-gear',
        route: null,
      },
    ]);
    
    // Computed properties from the store
    const name = computed(() => store.getters['auth/getUserName']);
    const account_type = computed(() => store.getters['auth/getUserType']);
    const isSidebarOpen = computed(() => store.getters['sidebar/isSidebarOpen']);
    
    // Actions to be dispatched
    const toggleSideBar = () => store.dispatch('sidebar/toggleSidebar');
    
    // Fetch notifications on component mount
    const fetchNotifications = async (isFromScroll = false) => {
      if (loading.value || !hasMore.value) return;

      loading.value = true;
      try {
        await store.dispatch('notifications/fetchNotifications', page.value);
        const response = store.getters['notifications/notifications'];
        
        if (isFromScroll) {
          notifications.value.data.push(...response.data);
        } else {
          notifications.value = response;
        }

        if (response.current_page < response.last_page) {
          page.value++;
        } else {
          hasMore.value = false;
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        loading.value = false;
      }
    };

    // Handle scroll in notifications dropdown
    const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        fetchNotifications(true);
      }
    };
    
    // Logout function
    const logOut = () => {
      try {
        store.dispatch('auth/logout');
        window.location.reload();
      } catch (error) {
        console.error('Logout failed:', error); // Handle errors appropriately
      }
    };

    // Navigate to the selected route
    const navigateTo = (tabRoute) => {
      if (currentRoute.path !== tabRoute && tabRoute) {
        if (tabRoute == '/profile') {
          const userId = store.getters['auth/getUserId'];
          router.push(`${tabRoute}/${userId}`);
        } else {
          router.push(tabRoute);
        }
      }
    };

    // Format date function
    const formatDate = (date) => {
      return new Date(date).toLocaleString();
    };

    // Fetch notifications when the component is mounted
    onMounted(() => {
      fetchNotifications();
    });

    const markAllNotificationsAsSeen = async ()  => {
      try{
        await store.dispatch('notifications/markAllNotificationsAsSeen');
        const response = store.getters['notifications/notifications'];
        notifications.value = response;
      }
      catch(e){
        console.log(e);
      }
    }

    return {
      markAllNotificationsAsSeen,
      is_notifications_box_open,
      notifications,
      actions,
      name,
      account_type,
      toggleSideBar,
      logOut,
      formatDate,
      navigateTo,
      handleScroll,
      fetchNotifications,
      loading,
    };
  },
};
</script>
<style lang="scss">
.logo{
  color: #7a7a7a;
}
.actions-main-list{
   list-style: none;
    display: flex;
    flex-direction: row-reverse;
    gap: 0px;
    align-items: center;
    margin: 0px;

}
.nasted-actions-list-container li{
  cursor: pointer;
  
}
.main-action{
    width: 30px;

    padding: 5px;
    color: #7a7a7a;
    height: 30px;
    border-radius: 100%;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: space-around;
    transition: 0.2s ease;
}
.main-action:hover { 
  background-color: #e0e0e0;
  color: #0b5572;
  transition: 0.2s ease;
}
.user_actions-list:hover ul{
  height: 240px;
  padding: 10px 0px;
  opacity: 1;
  transition: 0.3s ease-in-out;
}
.user-img-container{
  display: flex;
}
.user-img-container img{
  width: 35px;
}
.nasted-actions-list-container{
  position: relative;
}
.user-notification-container{
  position: relative;  
  .notification-container{
    width: 400px;
    height: 0px;
    position: absolute;
    right: 0px;
    top: 50px;
    z-index:10000;
    overflow: hidden;
    border-radius: 10px;
    background-color: #fff;
    box-shadow: 0px 0px 50px -5px #dbdbdb;
    transition: 0.3s ease-in-out;
    opacity: 0;
    &::-webkit-scrollbar {
        display: none; /* Hides scrollbar */
      }
    ul{
      overflow: hidden;
      text-align: left;
      list-style: none;
      padding: 0;
      li{
        padding: 15px 18px;
        color: #000;
        cursor: pointer;
        transition: 0.2s ease;
        &:hover{
          transition: 0.2s ease;
          background-color: #ececec;
        }

        p{
          font-size: 12px;
        }
        span{
          font-size: 14px;
          
        }
        .notification-header{
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
          span.notification-title{
            font-weight: bold;
          }
          .noti-date{
            font-size: 12px;
          }
        }
      }
    }
    .notification-container-footer{
      width: 100%;
      position: relative;
      background-color: #f8f8f8;
      bottom: -1px;
      button{
        background: none;
        color: #ababab;
        border: none;
        transition: 0.2s ease;
        font-size: 14px;
        &:hover{
          color: #000;
          transition: 0.2s ease;
        }
      }
    }
  }
  &:hover{
    .notification-container{
      height: 250px;
      overflow: scroll;
      opacity: 1;
      transition: 0.3s ease-in-out;
      
    }
  }
}
.nasted-actions-list{
  height: 0px;
  overflow: hidden;
  padding: 0px;
  list-style: none;
  background-color: #ffffff;
  border-radius: 5px;
  width: 180px;
  position: absolute;
  right: 0px;
  top: 15px;
  z-index:10000;
  opacity: 0;
  box-shadow: 0px 0px 50px -5px #dbdbdb;
  transition: 0.3s ease-in-out;
}
.nasted-actions-list li{
  padding: 5px 15px;
  padding-bottom: 0px;
  
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  transition:  0.5s ease-in-out;
}
.nasted-actions-list .action-tap {
  padding: 5px 15px;
  color: #9e9e9e;
}
.nasted-actions-list .action-tap:hover{
  background-color: #ebf2ff;
  color: #000;
  box-shadow: inset 0px 0px 11px -9px #000;
  transition:  0.5s ease-in-out;
}
/* Header */
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  z-index: 99999;
}


.user-box{
  display: flex;
  gap: 8px;
  border-bottom: solid 1px #ececec;
  padding: 0px 0px 10px 0px;
}

.user-details-box span {
  display: block;
  line-height: 12px;
}
.user-details-box .user-email{
  font-weight: 500;
  font-size: 12px;
}
.user-details-box .user-role{
  font-size: 12px;
  color: #0d426d;
  line-height: 30px;
}
</style>